import { useCallback, useMemo, useState } from 'react';
import config from '../config';
import { useSession } from '../contexts/SessionContext';
import { GitAdminCoverage, GitAdminIssue, GitAdminRepository, GitRefreshRequest, GitAdminTreeResponse, PagedApiResponse, GitIssueTimeEventsRequest, GitIssueTimeline, GitRefreshStatus, GitIssueRefreshResult, GitIterationWorkload } from '../interfaces';

type GitAdminListQuery = {
  index?: number;
  nbItems?: number;
  descOrder?: boolean;
};

const DEFAULT_LIST_QUERY: Required<GitAdminListQuery> = {
  index: 0,
  nbItems: 200,
  descOrder: false
};

const toQueryString = (query?: GitAdminListQuery): string => {
  const merged = { ...DEFAULT_LIST_QUERY, ...(query ?? {}) };
  const params = new URLSearchParams({
    index: String(merged.index),
    nbItems: String(merged.nbItems),
    descOrder: String(merged.descOrder)
  });

  return params.toString();
};

const useGitAdminApi = () => {
  const { sessionState } = useSession();
  const [activeRequests, setActiveRequests] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const authHeaders = useMemo(() => {
    const token = sessionState.account?.token;
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }, [sessionState.account?.token]);

  const fetchJson = useCallback(async <T,>(url: string, init?: RequestInit): Promise<T> => {
    setActiveRequests(prev => prev + 1);
    setError(null);
    try {
      const response = await fetch(url, {
        ...init,
        headers: {
          ...authHeaders,
          ...(init?.headers ?? {})
        }
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      return await response.json() as T;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown request error';
      setError(message);
      throw err;
    } finally {
      setActiveRequests(prev => Math.max(0, prev - 1));
    }
  }, [authHeaders]);

  const loading = activeRequests > 0;

  const getRepositories = useCallback(async () => {
    return fetchJson<PagedApiResponse<GitAdminRepository>>(`${config.centralApiBaseUrl}/git/repositories?index=0&nbItems=200&descOrder=false`);
  }, [fetchJson]);

  const getCoverages = useCallback(async (query?: GitAdminListQuery) => {
    return fetchJson<PagedApiResponse<GitAdminCoverage>>(`${config.centralApiBaseUrl}/git/coverages?${toQueryString(query)}`);
  }, [fetchJson]);

  const getIssues = useCallback(async (query?: GitAdminListQuery) => {
    return fetchJson<PagedApiResponse<GitAdminIssue>>(`${config.centralApiBaseUrl}/git/issues?${toQueryString(query)}`);
  }, [fetchJson]);

  const getTree = useCallback(async (request: GitRefreshRequest) => {
    return fetchJson<GitAdminTreeResponse>(`${config.centralApiBaseUrl}/git/tree`, {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }, [fetchJson]);

  const updateRepository = useCallback(async (repository: GitAdminRepository) => {
    return fetchJson<GitAdminRepository>(`${config.centralApiBaseUrl}/git/repositories`, {
      method: 'PUT',
      body: JSON.stringify(repository)
    });
  }, [fetchJson]);

  const updateCoverage = useCallback(async (coverage: GitAdminCoverage) => {
    return fetchJson<GitAdminCoverage>(`${config.centralApiBaseUrl}/git/coverages`, {
      method: 'PUT',
      body: JSON.stringify(coverage)
    });
  }, [fetchJson]);

  const updateIssue = useCallback(async (issue: GitAdminIssue) => {
    return fetchJson<GitAdminIssue>(`${config.centralApiBaseUrl}/git/issues`, {
      method: 'PUT',
      body: JSON.stringify(issue)
    });
  }, [fetchJson]);

  const refreshAll = useCallback(async (request: GitRefreshRequest) => {
    return fetchJson<{ operationId: string; status: string }>(`${config.centralApiBaseUrl}/git/refresh-all`, {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }, [fetchJson]);

  const getRefreshStatus = useCallback(async (operationId: string) => {
    return fetchJson<GitRefreshStatus>(
      `${config.centralApiBaseUrl}/git/refresh-all/${operationId}`,
      {
        method: 'GET'
      }
    );
  }, [fetchJson]);

  const getIssueTimeEvents = useCallback(async (request: GitIssueTimeEventsRequest) => {
    return fetchJson<GitIssueTimeline>(`${config.centralApiBaseUrl}/git/issue-time-events`, {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }, [fetchJson]);

  const refreshIssueApp = useCallback(async (request: GitIssueTimeEventsRequest) => {
    return fetchJson<GitIssueRefreshResult>(`${config.centralApiBaseUrl}/git/refresh-issue-app`, {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }, [fetchJson]);

  const getWorkloadByUser = useCallback(async (iteration: string, includeClosedIssues = true) => {
    const params = new URLSearchParams({
      iteration,
      includeClosedIssues: String(includeClosedIssues)
    });

    return fetchJson<GitIterationWorkload>(
      `${config.centralApiBaseUrl}/git/workload/users?${params.toString()}`,
      {
        method: 'GET'
      }
    );
  }, [fetchJson]);

  return {
    loading,
    error,
    getRepositories,
    getCoverages,
    getIssues,
    getTree,
    updateRepository,
    updateCoverage,
    updateIssue,
    refreshAll,
    getRefreshStatus,
    getIssueTimeEvents,
    refreshIssueApp,
    getWorkloadByUser
  };
};

export default useGitAdminApi;
